import React, { useEffect, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { Card } from '@mesh.ai/rabbit';
import { useSelector } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { Plus, OpenSidePanel, Delete } from 'assets/assetsV1/iconComponents';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, USER_ROLES } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button, BUTTON_VARIANT } from 'uiToolkitV1/Button';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { EMPLOYEE_ROSTER_COLUMN_KEYS, TABLE_COLUMNS_DISPLAYNAME } from './constants';
import { useGetEmployeeRosterList } from './dataHooks';
import { employeeCellRenderer, getCellValue } from './utils';

const ReviewListView = ({ gridRef }) => {
  const { data: revieweeListData } = useGetEmployeeRosterList();

  useEffect(() => {
    console.log(revieweeListData);
  }, [revieweeListData]);

  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);
  const USER_ROLE_MAP = USER_ROLES({ enabledFeatures }).reduce(
    (prev, { value, label }) => ({ ...prev, [value]: label }),
    {}
  );

  const columnDefs = useMemo(
    () =>
      EMPLOYEE_ROSTER_COLUMN_KEYS.map((columnKey) => ({
        colId: columnKey,
        field: columnKey,
        id: columnKey,
        displayName: TABLE_COLUMNS_DISPLAYNAME[columnKey],
        minWidth: 220,
        hide: false,
        flex: 1,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        headerValueGetter: () => TABLE_COLUMNS_DISPLAYNAME[columnKey],
        wrapText: true,
        autoHeight: true,
        sortable: true,
        valueGetter: (params) => {
          const value = getCellValue({
            columnKey,
            columnName: TABLE_COLUMNS_DISPLAYNAME[columnKey],
            cellData: params.data,
            userRoleMap: columnKey === 'employeeAccesses' ? USER_ROLE_MAP : null,
          })?.toLowerCase();
          return value;
        },
        cellRenderer: employeeCellRenderer,
        // menuTabs: ['filterMenuTab'],
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        filter: 'agTextColumnFilter',
        cellStyle: { display: 'flex', alignItems: 'center', height: 'auto' },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [EMPLOYEE_ROSTER_COLUMN_KEYS]
  );

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'companySettings';
    const tabId = 'manageEmployeeRoster';
    const uniqueId = 'EmployeeRosterGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const getEmployeeRowKebabMenuOption = () => {
    const options = [
      {
        label: 'View/Edit details',
        onClick: () => {},
        Icon: OpenSidePanel,
      },
      {
        label: 'Delete',
        onClick: () => {},
        Icon: Delete,
      },
    ];
    return options;
  };

  return (
    <Card className="review" style={{ padding: '12px' }}>
      <Box width="100%" marginBottom="8px" display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" flexDirection="row" alignItems="center">
          <AgGridSearchBar gridRef={gridRef} />
          {revieweeListData && (
            <Box borderLeft="1px solid #CFD7DF" ml="16px" pl="16px" h="20px">
              <TextHeader4 bold color="text.g2">
                {revieweeListData.length} Employees
              </TextHeader4>
            </Box>
          )}
        </Box>
        <Button variant={BUTTON_VARIANT.filled} StartIcon={Plus} color="blue" onClick={() => {}}>
          Add new
        </Button>
      </Box>
      <AgGridTable
        ref={gridRef}
        gridStatePersistenceKey={gridStatePersistenceKey}
        sideBar={sideBar}
        columnDefs={columnDefs}
        rowData={revieweeListData}
        kebabMenuIsRowSpecific
        kebabMenuOptionsGenerator={() => getEmployeeRowKebabMenuOption()}
        domLayout="autoHeight"
        animateRows={false}
        containerAutoHeight
        pagination
        paginationPageSize={10}
        showSidebarToggle={false}
        onCellClicked={() => {}}
      />
    </Card>
  );
};

export default ReviewListView;
